/**
 * Marca exibida pela SplashScreen.
 *
 * A splash (`BrandSplashScreen`) fica acima do `BrandProvider` na árvore — ela também cobre o
 * carregamento do usuário, antes de a marca vir da API —, então não consegue ler o
 * `BrandContext`. Esta store faz a ponte: o `BrandProvider` grava, a splash assina.
 *
 * A cópia também vai para o `localStorage`, para que ao recarregar a página a splash já abra
 * com a marca da última sessão. O logout limpa (`clearBrandSplash`), para não vazar a marca de
 * uma empresa para o próximo usuário do mesmo navegador.
 */
import { useSyncExternalStore } from 'react'
import { BrandConfigResponse } from './_models'

const BRAND_SPLASH_STORAGE_KEY = 'brand.splash'

export type BrandSplash = {
  logoUrl?: string
  fantasia?: string
  /** Cor do aside da marca — é sobre ela que o logo aparece no header, então é o fundo seguro */
  backgroundColor?: string
}

const asOptionalString = (value: unknown) => (typeof value === 'string' ? value : undefined)

const loadStoredBrandSplash = (): BrandSplash | undefined => {
  try {
    const stored = JSON.parse(localStorage.getItem(BRAND_SPLASH_STORAGE_KEY) || 'null')
    if (!stored || typeof stored !== 'object') return undefined

    return {
      logoUrl: asOptionalString(stored.logoUrl),
      fantasia: asOptionalString(stored.fantasia),
      backgroundColor: asOptionalString(stored.backgroundColor),
    }
  } catch {
    return undefined
  }
}

const storeBrandSplash = (brandSplash?: BrandSplash) => {
  try {
    if (brandSplash) localStorage.setItem(BRAND_SPLASH_STORAGE_KEY, JSON.stringify(brandSplash))
    else localStorage.removeItem(BRAND_SPLASH_STORAGE_KEY)
  } catch {}
}

let currentBrandSplash: BrandSplash | undefined = loadStoredBrandSplash()
const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = () => currentBrandSplash

export const saveBrandSplash = (brand: BrandConfigResponse | null) => {
  currentBrandSplash = brand
    ? {
        logoUrl: brand.logo?.url,
        fantasia: brand.fantasia || brand.nome,
        backgroundColor: brand.layout?.asideColor,
      }
    : undefined
  storeBrandSplash(currentBrandSplash)
  listeners.forEach((listener) => listener())
}

export const clearBrandSplash = () => saveBrandSplash(null)

export const useBrandSplash = () => useSyncExternalStore(subscribe, getSnapshot)
